import { useState, useEffect, createContext, useContext } from 'react';
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { supabase as supabaseClient } from '@/integrations/supabase/client';

// This context will provide the Supabase client to all components
const SupabaseContext = createContext<SupabaseClient | null>(null);

// Provider component that wraps your app and makes the Supabase client available
export const SupabaseProvider = ({ children }: { children: React.ReactNode }) => {
  const [supabase] = useState(() => supabaseClient);
  
  return (
    <SupabaseContext.Provider value={supabase}>
      {children}
    </SupabaseContext.Provider>
  );
};

// Hook to access the Supabase client
export const useSupabaseClient = (): SupabaseClient => {
  const context = useContext(SupabaseContext);
  
  if (!context) {
    throw new Error('useSupabaseClient must be used within a SupabaseProvider');
  }
  
  return context;
};

// Hook to check if the current user is authenticated
export const useAuth = () => {
  const supabase = useSupabaseClient();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up listener for auth state changes FIRST
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email);
        if (session?.user) {
          setUser(session.user);
        } else {
          setUser(null);
        }
        setLoading(false);
      }
    );

    // THEN check for existing session
    const getUser = async () => {
      setLoading(true);
      
      try {
        const { data } = await supabase.auth.getSession();
        console.log('Current session:', data.session);
        
        if (data.session) {
          const { data: userData } = await supabase.auth.getUser();
          setUser(userData.user);
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Error loading user:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    getUser();

    return () => {
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, [supabase]);

  return { user, loading };
};

// This is a mock implementation that will be used if the real client fails
export const mockSupabaseClient = {
  auth: {
    signUp: async ({ email, password, options }: any) => {
      console.log('Mock sign up', { email, password, options });
      return { error: null };
    },
    signInWithPassword: async ({ email, password }: any) => {
      console.log('Mock sign in', { email, password });
      return { error: null };
    },
    signOut: async () => {
      console.log('Mock sign out');
      return { error: null };
    },
    getUser: async () => {
      console.log('Mock get user');
      return { 
        data: { 
          user: {
            id: '123',
            email: 'user@example.com',
            user_metadata: {
              name: 'Test User',
              company: 'Test Company',
            }
          } 
        }, 
        error: null 
      };
    },
    getSession: async () => {
      console.log('Mock get session');
      return { 
        data: { 
          session: {
            user: {
              id: '123',
              email: 'user@example.com',
              user_metadata: {
                name: 'Test User',
                company: 'Test Company',
              }
            }
          } 
        }, 
        error: null 
      };
    },
    updateUser: async (data: any) => {
      console.log('Mock update user', data);
      return { error: null };
    },
    resetPasswordForEmail: async (email: string, options: any) => {
      console.log('Mock reset password', { email, options });
      return { error: null };
    },
    onAuthStateChange: (callback: any) => {
      console.log('Mock auth state change listener');
      return { data: { subscription: { unsubscribe: () => {} } } };
    }
  },
  from: (table: string) => ({
    insert: (data: any) => ({
      select: () => {
        console.log(`Mock insert into ${table}`, data);
        return { data, error: null };
      }
    }),
    select: () => {
      console.log(`Mock select from ${table}`);
      return { data: [], error: null };
    },
    update: (data: any) => ({
      eq: (column: string, value: any) => {
        console.log(`Mock update ${table} where ${column} = ${value}`, data);
        return { data: [], error: null };
      }
    }),
    delete: () => ({
      eq: (column: string, value: any) => {
        console.log(`Mock delete from ${table} where ${column} = ${value}`);
        return { data: [], error: null };
      }
    })
  }),
  storage: {
    from: (bucket: string) => ({
      upload: (path: string, file: any) => {
        console.log(`Mock upload to ${bucket}/${path}`);
        return { data: { path }, error: null };
      },
      getPublicUrl: (path: string) => {
        console.log(`Mock get public URL for ${bucket}/${path}`);
        return { data: { publicUrl: `https://example.com/${bucket}/${path}` } };
      }
    })
  }
};

// Use this only as a fallback
export const useMockSupabaseClient = () => mockSupabaseClient as unknown as SupabaseClient;
